<template>
  <section class="section" id="feedbacks-section">
    <div class="container">
      <b-loading
        :is-full-page="true"
        :active="isLoading"
        :can-cancel="false"
      ></b-loading>

      <feedbacks-title
        :feedbacksQty="feedbacks.length"
        :storeId="selectedStoreId"
        :storeName="selectedStoreName"
        @get-feedbacks="getFeedbacks"
        @update-dates="updateDates"
        @update-store="updateStore"
        @download-feedbacks-report="downloadFeedbacksReport"
      />

      <h2
        v-if="feedbacks.length === 0"
        class="subtitle has-text-centered is-spaced"
        style="margin-top: 5rem;"
      >
        {{ $t("feedback.noFeedbacksAtThisDate") }}
      </h2>

      <div v-else>
        <h2 class="subtitle has-text-centered">
          {{ `${$t("feedback.average")}: ${average}` }}
        </h2>

        <feedbacks-filter />

        <p
          v-if="filteredFeedbacks.length === 0"
          class="subtitle has-text-centered"
        >
          {{ $t("feedback.noFeedbacksMatchFilter") }}
        </p>

        <div v-else class="columns is-multiline">
          <feedback
            v-for="f in filteredFeedbacks"
            :key="f._id"
            :feedback="f"
            @change-feedback-status="changeFeedbackStatus"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { common } from "../../mixins/common";
import { mapGetters } from "vuex";
import feedbacksTitle from "./FeedbacksTitle";
import feedback from "./Feedback";
import feedbacksFilter from "./FeedbacksFilter";

export default {
  mixins: [common],

  components: {
    feedbacksTitle,
    feedback,
    feedbacksFilter
  },

  data() {
    return {
      isLoading: false,
      dates: [],
      store: undefined,
      storeName: null,
      ratingFilter: [],
      statusFilter: []
    };
  },

  methods: {
    getFeedbacks() {
      this.isLoading = true;
      this.$store
        .dispatch("getFeedbacks", {
          storeId: this.store,
          dates: this.dates
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    updateDates(dates) {
      this.dates[0] = dates
        ? this.moment(dates[0]).format("YYYY-MM-DD")
        : this.moment().format("YYYY-MM-DD");

      this.dates[1] = dates
        ? this.moment(dates[1]).format("YYYY-MM-DD")
        : this.moment().format("YYYY-MM-DD");
    },

    downloadFeedbacksReport() {
      this.isLoading = true;
      this.$store
        .dispatch("downloadFeedbacksReport", {
          storeName: this.storeName,
          storeId: this.store,
          dates: this.dates
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    updateStore(store) {
      this.store = store ? store.split("|")[0] : "";
      this.storeName = store
        ? store.split("|")[1]
        : this.$t("common.allStores");
    },

    changeFeedbackStatus(data) {
      this.isLoading = true;
      this.$store
        .dispatch("changeFeedbackStatus", data)
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$forceUpdate();
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  },

  computed: {
    ...mapGetters(["storeBeingManaged", "feedbacks"]),

    isAllStoresMode() {
      return this.$route.query.allStores === "true";
    },

    selectedStoreId() {
      return this.isAllStoresMode ||
        !this.storeBeingManaged ||
        !this.storeBeingManaged._id
        ? null
        : this.storeBeingManaged._id;
    },

    selectedStoreName() {
      return this.selectedStoreId && this.storeBeingManaged.storeInfo
        ? this.storeBeingManaged.storeInfo.name
        : this.$t("common.allStores");
    },

    filteredFeedbacks() {
      const ratingFilteredFeedbacks =
        this.ratingFilter.length === 0
          ? this.feedbacks
          : this.feedbacks.filter(f => {
              return this.ratingFilter.includes(f.rating);
            });

      const statusFilteredFeedbacks =
        this.statusFilter.length === 0
          ? ratingFilteredFeedbacks
          : ratingFilteredFeedbacks.filter(f => {
              return this.statusFilter.includes(f.status);
            });

      return statusFilteredFeedbacks;
    },

    average() {
      const sum = this.filteredFeedbacks.reduce(
        (total, f) => (f.rating ? f.rating + total : total),
        0
      );

      const feedbacksWithRating = this.filteredFeedbacks.filter(f => {
        return !isNaN(f.rating);
      });

      return feedbacksWithRating.length === 0
        ? "-"
        : (sum / feedbacksWithRating.length).toFixed(2);
    }
  },

  mounted() {
    this.updateDates();
    this.getFeedbacks();
  },

  created() {
    this.store = this.selectedStoreId || "";
    this.storeName = this.selectedStoreName;
  }
};
</script>

<style scoped>
#feedbacks-section {
  margin-top: 3rem;
  min-height: 94vh;
  padding: 2rem 1rem;
  background-color: hsl(0, 0%, 96%);
}
</style>
